import { Seo } from '@/lib/metadata/getMeta';
import { PostsACF } from '@/types/blog/single';
import { PostRatings } from '@/types/blog/single';
import { TutorialBlock } from '@/types/exchanges/page';

// Contact Website
export interface ContactWebsite {
    target: string;
    title: string;
    url: string;
}

// Contact
export interface Contact {
    address: string;
    email: string;
    phone: string;
    website: ContactWebsite;
}

// CTA Button
export interface CtaButton {
    target: string;
    title: string;
    url: string;
}

// Rating
export interface Rating {
    title: string;
    value: number;
}

// Tutorial Image
export interface TutorialImage {
    title: string;
    sourceUrl: string;
    mediaDetails: {
        height: number;
        width: number;
    };
}

// Tutorial
export interface Tutorial {
    description: string;
    title: string;
    image: TutorialImage;
}

// Tutorial Section
export interface TutorialSection {
    title: string;
    tutorial: Tutorial;
}

// New Fields
export interface NewFields {
    logo: {
        altText: string;
        title: string;
        sourceUrl: string;
        mediaDetails: {
            height: number;
            width: number;
        };
    };
    ctaWebsite: {
        title: string;
        url: string;
        target: string;
    };
    sponsored: boolean;
    verified: boolean;
    about: string;
    badge: string;
    enName: string;
    faName: string;
    subtitle: string;
    extraButtons: string[];
    ctaId: string;
    moreButtonId: string;
    contact: Contact;
    ctaButton: CtaButton;
    rank: number;
    rating: Rating[];
    tutorials: TutorialSection[];
}

// Broker Single
export interface BrokerSingle {
    newfields: NewFields;
}

// Broker By
export interface BrokerBy {
    title: string;
    content: string;
    date: string;
    databaseId: number;
    commentCount: number;
    slug: string;
    posts_acf: PostsACF;
    seo: Seo;
    postRatings: PostRatings;
    brokerSingle: BrokerSingle;
}

// utils/labels.ts
export const farsiLabels: Record<string, string> = {
    farsiSupport: 'پشتیبانی فارسی',
    fieldGroupName: 'نام گروه فیلد',
    minimumDeposit: 'حداقل سپرده',
    rialSupport: 'پشتیبانی ریالی',
    year: 'سال',
    customFeature: 'ویژگی سفارشی',
    iranSupport: 'پشتیبانی ایران',
    farsiSite: 'سایت فارسی',
    eaUse: 'امکان استفاده از EA',
    ecnAccount: 'حساب ECN',
    insurance: 'بیمه سپرده',
    telTrade: 'معامله تلفنی',
    identification: 'احراز هویت آسان',
    tradeNews: 'معامله در تایم خبر',
    traditional: 'حساب سنتی',
    copyTrading: 'کپی تریدینگ',
    // Account Labels
    title: 'عنوان حساب',
    activity: 'کارمزد عدم فعالیت',
    commission: 'کمیسیون',
    islamic: 'حساب اسلامی',
    levelMarginCall: 'لول مارجین کال',
    leverage: 'لوریج',
    manageSystemOrders: 'سیستم مدیریت سفارشات',
    maxOpenOrders: 'حداکثر سفارشات باز',
    minDeposite: 'حداقل واریز',
    platform: 'پلتفرم معاملاتی',
    requote: 'ریکوت',
    spread: 'اسپرد',
    stopLevel: 'استاپ لول',
    stopOut: 'استاپ اوت',
    typeManageSystemSpread: 'نوع سیستم مدیریت اسپرد',
    typeOrders: 'نوع اجرای سفارشات',
    withoutCommission: 'حساب بدون کمیسیون',
};

export interface CustomFeature {
    title: string;
    value: string;
}

export interface GeneralInfo {
    farsiSupport: boolean;
    fieldGroupName: string;
    minimumDeposit: number;
    minimumDepositType: string;
    rialSupport: boolean;
    year: number;
    coinsNum?: number;
    customFeature: CustomFeature[];
    iranSupport: boolean;
    eaUse: boolean;
    farsiSite: boolean;
    ecnAccount: boolean;
    insurance: boolean;
    telTrade: boolean;
    identification: boolean;
    tradeNews: boolean;
    traditional: boolean;
    copyTrading: boolean;
    rank: number;
    brokerRating: {
        finalRating: number;
    };
}

export interface Account {
    title: string;
    detail: AccountDetail;
}
export interface Brokers {
    brokerFields: BrokerFields;
}
export interface BrokerFields {
    generalInfo: GeneralInfo;
    account: Account | Account[];
    tutorialBlock: TutorialBlock[];
    tutorialTitle?: string;
}

export interface AccountDetail {
    activity: boolean;
    commission: number;
    islamic: boolean;
    levelMarginCall: number;
    leverage: number;
    manageSystemOrders: boolean;
    maxOpenOrders: number;
    minDeposite: number;
    platform: string[];
    requote: boolean;
    spread: number;
    stopLevel: string;
    stopOut: number;
    title: string;
    typeManageSystemSpread: string;
    typeOrders: string;
    withoutCommission: boolean;
}
